import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useFocusTrap } from '@voll-intelligence/intelligence-ui-kit';
import { useGenUiI18n } from '../../shared/ui/i18n.js';
import type { PlaceDetails, PlacesPort } from '@voll-intelligence/genui-contract';
import { mapsLink, PlaceFacts } from './place_facts.js';
import { PlacePhoto } from './place_photo.js';
function PlaceReviews({ reviews }: { readonly reviews: PlaceDetails['reviews'] }) {
  const { t } = useGenUiI18n();
  if (reviews.length === 0) return null;
  return (
    <section>
      <h3 className="mb-2 font-semibold">{t('consultPlaces.reviews')}</h3>
      <ul className="space-y-3">
        {reviews.map((review, index) => (
          <li key={`${review.text}:${String(index)}`} className="rounded-xl bg-muted p-3 text-sm">
            <p>{review.text}</p>
            {(review.authorAttribution?.displayName ?? review.relativeTime) && (
              <p className="mt-2 text-xs text-muted-foreground">
                {review.authorAttribution?.uri && review.authorAttribution.displayName ? (
                  <a href={review.authorAttribution.uri} target="_blank" rel="noreferrer">
                    {review.authorAttribution.displayName}
                  </a>
                ) : (
                  review.authorAttribution?.displayName
                )}
                {review.relativeTime ? ` · ${review.relativeTime}` : ''}
              </p>
            )}
            {review.googleMapsUri && (
              <a
                className="mt-2 inline-block text-xs font-semibold"
                href={review.googleMapsUri}
                target="_blank"
                rel="noreferrer"
              >
                {t('consultPlaces.sourceGoogle')}
              </a>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

export function PlaceSheet({
  place,
  port,
  onClose,
  onRetry,
  loading,
  failed,
}: {
  readonly place: PlaceDetails;
  readonly port: PlacesPort;
  readonly onClose: () => void;
  readonly onRetry: () => void;
  readonly loading: boolean;
  readonly failed: boolean;
}) {
  const { t } = useGenUiI18n();
  const panelRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  useFocusTrap(panelRef, true);
  useEffect(() => {
    closeRef.current?.focus();
    const keydown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', keydown);
    return () => {
      document.removeEventListener('keydown', keydown);
    };
  }, [onClose]);
  return createPortal(
    <div className="genui2 fixed inset-0 z-50 flex items-end bg-foreground/50 sm:items-center sm:justify-center">
      <button className="absolute inset-0" aria-label={t('common.close')} onClick={onClose} />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={place.name}
        // Colada ao rodapé no telefone: o fim do conteúdo sobe da barra de gestos (#3366).
        className="relative z-10 max-h-[88dvh] w-full overflow-y-auto rounded-t-3xl bg-card scrollbar-rail p-5 pr-[calc(1.25rem-var(--scrollbar-rail))] pb-[calc(1.25rem+env(safe-area-inset-bottom,0px))] shadow-xl sm:max-w-2xl sm:rounded-3xl sm:pb-5"
      >
        <header className="mb-4 flex items-start justify-between gap-4">
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              {place.primaryTypeDisplayName ?? t('consultPlaces.place')}
            </p>
            <h2 className="text-xl font-semibold text-foreground">{place.name}</h2>
            <PlaceFacts place={place} />
          </div>
          <button
            ref={closeRef}
            type="button"
            className="rounded-full border border-border px-3 py-2 text-sm"
            onClick={onClose}
          >
            {t('common.close')}
          </button>
        </header>

        {loading && <p role="status">{t('consultPlaces.loadingDetails')}</p>}
        {failed && (
          <div role="alert" className="rounded-xl bg-destructive/10 p-4">
            <p>{t('consultPlaces.detailsError')}</p>
            <button type="button" className="mt-2 font-semibold" onClick={onRetry}>
              {t('common.tryAgain')}
            </button>
          </div>
        )}
        {!loading && !failed && (
          <div className="space-y-5">
            {place.photos.length > 0 && (
              <section
                aria-label={t('consultPlaces.photos')}
                className="flex snap-x gap-3 overflow-x-auto"
              >
                {place.photos.map((photo) => (
                  <PlacePhoto key={photo.name} photo={photo} place={place} port={port} />
                ))}
              </section>
            )}
            {place.editorialSummary && (
              <p className="text-sm leading-6 text-muted-foreground">{place.editorialSummary}</p>
            )}
            <dl className="grid gap-3 text-sm sm:grid-cols-2">
              {place.address && (
                <div>
                  <dt className="font-semibold">{t('consultPlaces.address')}</dt>
                  <dd>{place.address}</dd>
                </div>
              )}
              {place.phoneNumber && (
                <div>
                  <dt className="font-semibold">{t('consultPlaces.phone')}</dt>
                  <dd>
                    <a href={`tel:${place.phoneNumber}`}>{place.phoneNumber}</a>
                  </dd>
                </div>
              )}
              {place.websiteUri && (
                <div>
                  <dt className="font-semibold">{t('consultPlaces.website')}</dt>
                  <dd>
                    <a href={place.websiteUri} target="_blank" rel="noreferrer">
                      {t('consultPlaces.openWebsite')}
                    </a>
                  </dd>
                </div>
              )}
            </dl>
            {place.weekdayHours && (
              <section>
                <h3 className="mb-2 font-semibold">{t('consultPlaces.hours')}</h3>
                <ul className="space-y-1 text-sm text-muted-foreground">
                  {place.weekdayHours.map((line) => (
                    <li key={line}>{line}</li>
                  ))}
                </ul>
              </section>
            )}
            <PlaceReviews reviews={place.reviews} />
            <a
              className="inline-flex rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground"
              href={mapsLink(place)}
              target="_blank"
              rel="noreferrer"
            >
              {t('consultPlaces.openGoogleMaps')}
            </a>
            {place.googleMapsUri && (
              <p className="text-xs text-muted-foreground">
                <a
                  className="underline underline-offset-2"
                  href={place.googleMapsUri}
                  target="_blank"
                  rel="noreferrer"
                >
                  {t('consultPlaces.sourcePlaceGoogle')}
                </a>
              </p>
            )}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
